import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PlusIcon, CheckIcon, ChevronDownIcon } from 'lucide-react';
import { CateringOffer, formatPrice, formatPriceExact, exVat } from '../data/smaken';
import { useRequest } from '../CartContext';
interface CateringCardProps {
  offer: CateringOffer;
  /** Hide the partner line when the card already sits on that partner's page. */
  showPlace?: boolean;
}
export function CateringCard({
  offer,
  showPlace = true
}: CateringCardProps) {
  const {
    add
  } = useRequest();
  const [added, setAdded] = useState(false);
  const [showContents, setShowContents] = useState(false);
  useEffect(() => {
    if (!added) return;
    const timer = window.setTimeout(() => setAdded(false), 1500);
    return () => window.clearTimeout(timer);
  }, [added]);
  return <article className="group flex h-full flex-col border-t-2 border-black pt-4" data-id="element-368" data-mp="22jyt2n8fv1wv">
      {/* Shared paper mat + one colour grade: photos from different kitchens read as one set */}
      <div className="relative aspect-[4/3] w-full bg-[#ECE5D8] p-3 sm:p-4" data-id="element-369" data-mp="pv85n09aqf0j">
        <div className="h-full w-full overflow-hidden" data-id="element-370" data-mp="kxf3qjf10762">
          <img src={offer.image} alt={offer.name} loading="lazy" style={offer.imageFocus ? {
          objectPosition: offer.imageFocus
        } : undefined} className="h-full w-full object-cover saturate-[0.9] sepia-[0.08] transition-transform duration-[280ms] ease-out group-hover:scale-[1.03]" data-id="element-371" data-mp="kwwxqv2kaeky" />
        </div>
        <span className="absolute left-0 top-0 bg-papper px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide2" data-id="element-372" data-mp="1zcjehxfayel">
          {offer.category}
        </span>
      </div>

      <div className="flex flex-1 flex-col pt-4" data-id="element-373" data-mp="az8zhv5qyoy1">
        {showPlace && <Link to={`/plats/${offer.placeSlug}`} className="text-[11px] font-bold uppercase tracking-wide2 text-rod transition-colors duration-150 ease-out hover:text-black" data-id="element-374" data-mp="b1byv5sshuff">
            {offer.placeName} · {offer.placeCity}
          </Link>}
        <h3 className="mt-2 font-display text-2xl leading-tight" data-id="element-375" data-mp="k2ues2g8evf3">{offer.name}</h3>
        <p className="mt-2 text-[0.95rem] leading-relaxed text-black/70" data-id="element-376" data-mp="oy9rgr2i6l9x">{offer.description}</p>

        {offer.diets && offer.diets.length > 0 && <ul className="mt-3 flex flex-wrap gap-1.5" aria-label="Specialkost" data-id="element-377" data-mp="y097bg1s7jba">
            {offer.diets.map(d => <li key={d} className="border border-skog px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide2 text-skog" data-id="element-378" data-mp="1b1o4eomxmaj">
                {d}
              </li>)}
          </ul>}

        <button type="button" onClick={() => setShowContents(s => !s)} aria-expanded={showContents} className="mt-3 flex items-center gap-1.5 self-start text-[11px] font-bold uppercase tracking-wide2 text-black/55 transition-colors duration-150 ease-out hover:text-black" data-id="element-379" data-mp="a9vpeicwv0uf">
          Innehåll & allergener
          <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform duration-200 ease-out ${showContents ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-380" data-mp="3b77gtimvuoxe" />
        </button>
        {showContents && <div className="mt-2 border-l-2 border-black/15 pl-3 text-sm leading-relaxed text-black/70" data-id="element-381" data-mp="3189ummcxj7hk">
            {offer.ingredients && <p data-id="element-382" data-mp="2x6wpkehnv8bp">{offer.ingredients}</p>}
            <p className={offer.ingredients ? 'mt-2' : ''} data-id="element-383" data-mp="2s8d1a7flhkg6">
              {offer.allergens === undefined ? <span className="text-black/50" data-id="element-384" data-mp="2nhwn2l3y9usr">Allergener kompletteras — ange specialkost i förfrågan.</span> : offer.allergens.length > 0 ? <>
                  <span className="font-semibold text-rod" data-id="element-385" data-mp="2ig7a2zx63721">Innehåller:</span> {offer.allergens.join(', ')}
                </> : <span className="font-semibold" data-id="element-386" data-mp="29gtzu517yeg9">Fri från de 14 vanligaste allergenerna.</span>}
            </p>
          </div>}

        <p className="mt-3 text-[11px] font-semibold uppercase tracking-wide2 text-black/45" data-id="element-387" data-mp="24l4wrrpzqff7">
          {offer.occasions.join(' · ')}
        </p>
        <p className="mt-1 text-[11px] font-semibold uppercase tracking-wide2 text-black/45" data-id="element-388" data-mp="chhebfhinef3">
          {offer.minGuests > 1 && <>Från {offer.minGuests} {offer.unit === 'per person' ? 'pers' : 'st'} · </>}
          Beställ {offer.leadTime} {offer.leadTime === 1 ? 'dag' : 'dagar'} innan
        </p>

        <div className="mt-auto flex items-end justify-between gap-4 pt-5" data-id="element-389" data-mp="3kwrjwxkm71rm">
          {offer.onRequest ? <p data-id="element-390" data-mp="3pwx7a1juq23w">
              <span className="font-display text-2xl" data-id="element-391" data-mp="3pxp0x6tw1prj">Vid förfrågan</span>
              <span className="mt-0.5 block text-[11px] uppercase tracking-wide2 text-black/45" data-id="element-392" data-mp="3bxed6cnvkral">
                Pris efter önskemål
              </span>
            </p> : <p data-id="element-393" data-mp="1a4gazq87kmcz">
              <span className="font-display text-2xl" data-id="element-394" data-mp="1a619ts1k2rgn">{formatPrice(offer.price)}</span>
              <span className="ml-1.5 text-[11px] uppercase tracking-wide2 text-black/45" data-id="element-395" data-mp="1f4ckg4zod5zx">{offer.unit}</span>
              <span className="mt-0.5 block text-[11px] uppercase tracking-wide2 text-black/45" data-id="element-396" data-mp="1p92msbyzqc4o">
                {formatPriceExact(exVat(offer.price))} exkl. moms
              </span>
            </p>}
          <button onClick={() => {
          add(offer);
          setAdded(true);
        }} aria-label={`Lägg ${offer.name} i förfrågan`} className={`flex shrink-0 items-center gap-2 px-4 py-3 text-[11px] font-bold uppercase tracking-wide2 transition-colors duration-150 ease-out ${added ? 'bg-skog text-papper' : 'bg-black text-papper hover:bg-rod'}`} data-id="element-397" data-mp="1y14lo5tevkcu">
            {added ? <CheckIcon className="h-4 w-4" data-id="element-398" data-mp="2cxvc3pl3xlqb" /> : <PlusIcon className="h-4 w-4" data-id="element-399" data-mp="2cyn9n5y9ltj8" />}
            {added ? 'Tillagd' : 'Lägg till'}
          </button>
        </div>
      </div>
    </article>;
}